import type { ChangeEventHandler } from 'react';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import * as HOOKS from '@siloreal:shared:hooks';

const EXCEL_FILE_EXTENSION = '.xlsx';
const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ACCEPTED_EXCEL_MIME_TYPES = new Set([EXCEL_MIME_TYPE, 'application/octet-stream', 'application/zip']);
const MAX_CLOSURE_FILE_SIZE_BYTES = 5 * 1024 * 1024;

function validateClosureFile(file: File): string | null {
  const hasExcelExtension = file.name.toLowerCase().endsWith(EXCEL_FILE_EXTENSION);
  const hasAcceptedMimeType = file.type === '' || ACCEPTED_EXCEL_MIME_TYPES.has(file.type);
  const i18n = HOOKS.useLocale.getState();

  if (!hasExcelExtension || !hasAcceptedMimeType) {
    return `${i18n.t('temp.common.toasts.error.select_an_excel_file')} .xlsx.`;
  }

  if (file.size === 0) {
    return i18n.t('temp.common.toasts.error.empty_file');
  }

  if (file.size > MAX_CLOSURE_FILE_SIZE_BYTES) {
    return i18n.t('temp.common.toasts.error.file_larger_than_5mb');
  }

  return null;
}

export function useClosureActions() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [closureIds, setClosureIds] = useState<string[] | null>(null);

  const openFilePicker = (): void => {
    if (!isReadingFile) fileInputRef.current?.click();
  };

  const openClosureModal = (silobagIds: readonly string[]): void => {
    const uniqueIds = [...new Set(silobagIds.map((id) => id.trim()).filter(Boolean))];
    if (uniqueIds.length > 0) setClosureIds(uniqueIds);
  };

  const downloadTemplate = async (): Promise<void> => {
    if (isGeneratingTemplate) return;

    const i18n = HOOKS.useLocale.getState();

    setIsGeneratingTemplate(true);
    const loadingToastId = toast.loading(
      `${i18n.t('temp.common.toasts.loading.generating_closure_spreadsheets')} ...`
    );

    try {
      const { generateSilobagClosureTemplate } =
        await import('@siloreal:shared:sdk/excel/silobag-closure-excel-generator');

      await generateSilobagClosureTemplate();
      toast.success(i18n.t('temp.common.toasts.success.download_closure_spreadsheets'), {
        id: loadingToastId,
      });
    } catch {
      toast.error(i18n.t('temp.common.toasts.error.closure_spreadsheet_generation'), { id: loadingToastId });
    } finally {
      setIsGeneratingTemplate(false);
    }
  };

  const uploadClosures: ChangeEventHandler<HTMLInputElement> = async (event) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';

    if (!file) return;

    const validationError = validateClosureFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    const i18n = HOOKS.useLocale.getState();

    setIsReadingFile(true);
    const loadingToastId = toast.loading(
      `${i18n.t('temp.common.toasts.loading.validating_closure_spreadsheets')} ...`
    );

    try {
      const { readSilobagClosureIds } =
        await import('@siloreal:shared:sdk/excel/silobag-closure-excel-reader');
      const silobagIds = await readSilobagClosureIds(file);

      toast.success(i18n.t('temp.common.toasts.success.check_silobags_spreadsheet'), {
        id: loadingToastId,
      });
      openClosureModal(silobagIds);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : i18n.t('temp.common.error.failed_to_read_closure_spreadsheet');
      toast.error(message, { id: loadingToastId });
    } finally {
      setIsReadingFile(false);
    }
  };

  return {
    fileInputRef,
    inputAccept: `${EXCEL_FILE_EXTENSION},${EXCEL_MIME_TYPE}`,
    isDownloading: isGeneratingTemplate,
    isUploading: isReadingFile,
    isModalOpen: closureIds !== null,
    closureIds,
    closeClosureModal: () => setClosureIds(null),
    openClosureModal,
    openFilePicker,
    downloadTemplate: () => void downloadTemplate(),
    uploadClosures,
  };
}
